import { DateType, FixedDate, IntervalDate } from '../models/date.model';
import moment from 'moment';

export class DateUtils {

  public static formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    // eslint-disable-next-line no-inline-comments
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11, so we add 1
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  public static formatDateToDDMMYYYY(date: Date): string {
    const year = date.getFullYear();
    // eslint-disable-next-line no-inline-comments
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11, so we add 1
    const day = String(date.getDate()).padStart(2, '0');

    return `${day}/${month}/${year}`;
  }

  public static parseYYYYMMDDToDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  public static getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public static isFixedDate(date: FixedDate | IntervalDate | undefined): date is FixedDate {
    return date?.type === DateType.FIXED_DATE;
  }

  public static isIntervalDate(date: FixedDate | IntervalDate | undefined): date is IntervalDate {
    return date?.type === DateType.INTERVAL_DATE;
  }

  /**
   * Compares two dates (ignoring time) and returns if they are the same day, month, and year.
   * @param {string | Date} date1 - The first date to compare.
   * @param {string | Date} date2 - The second date to compare.
   * @returns {boolean} - Returns true if the dates have the same day, month, and year, otherwise false.
   */
  static compareDates(date1: string | Date, date2: string | Date): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }

  public static getToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  /**
   * Normalize a FixedDate or IntervalDate by converting any string dates to Date objects.
   */
  public static normalizeDateValue<T extends FixedDate | IntervalDate | null | undefined>(value: T): T {
    if (!value) {
      return value;
    }

    if (this.isFixedDate(value)) {
      // eslint-disable-next-line no-nested-ternary
      const date = value.date instanceof Date ?
        value.date :
        typeof value.date === 'string' ?
          this.parseYYYYMMDDToDate(value.date) :
          null;

      return {
        ...value,
        date
      } as T;
    }

    if (this.isIntervalDate(value)) {
      // eslint-disable-next-line no-nested-ternary
      const start = value.start instanceof Date ?
        value.start :
        typeof value.start === 'string' ?
          this.parseYYYYMMDDToDate(value.start) :
          null;

      // eslint-disable-next-line no-nested-ternary
      const end = value.end instanceof Date ?
        value.end :
        typeof value.end === 'string' ?
          this.parseYYYYMMDDToDate(value.end) :
          null;

      return {
        ...value,
        start,
        end
      } as T;
    }

    return value;
  }

  /**
   * Determines whether a given `FixedDate` or `IntervalDate` ends strictly before a specified reference date.
   *
   * - For `FixedDate`: compares the `date` value.
   * - For `IntervalDate`: compares the `end` value of the interval.
   * - The comparison is done at **day-level precision** (ignores time).
   *
   * @param dateValue - The date instance to evaluate (either `FixedDate` or `IntervalDate`).
   * @param referenceDate - The date used as a threshold to compare against.
   * @returns `true` if the date value ends strictly before the reference date (day-wise), otherwise `false`.
   *
   * @example
   * DateUtils.isDateStrictlyBeforeReferenceDate({ type: DateType.FIXED_DATE, date: new Date('2024-01-01') }, new Date('2024-01-02'));
   * // true
   *
   * @example
   * DateUtils.isDateStrictlyBeforeReferenceDate({ type: DateType.INTERVAL_DATE, start: new Date('2024-01-01'), end: new Date('2024-01-03') }, new Date('2024-01-04'));
   * // true
   */
  public static isDateStrictlyBeforeReferenceDate(
    dateValue: FixedDate | IntervalDate | null | undefined,
    referenceDate: Date
  ): boolean {
    if (!dateValue || !referenceDate) {
      return false;
    }

    const normalized = this.normalizeDateValue(dateValue);

    if (this.isFixedDate(normalized) && normalized.date) {
      return moment(normalized.date).isBefore(moment(referenceDate).startOf('day'));
    }

    if (this.isIntervalDate(normalized) && normalized.end) {
      return moment(normalized.end).isBefore(moment(referenceDate).startOf('day'));
    }

    return false;
  }
  
}

